"use client";

import horarios from "@/app/horarios/horarios";
import { useState } from "react";

type Aula = {
    horario: string;
    aula: string;
    professor: string;
};

type Dia = {
    dia: string;
    aulas: Aula[];
};

const tabs = ["Kids", "Teens", "Adultos"] as const;

type Tab = (typeof tabs)[number];

const filtrarAulas = (filtro: (nome: string) => boolean) =>
    horarios
        .map((dia) => ({
            ...dia,
            aulas: dia.aulas.filter((aula) =>
                filtro(aula.aula.toLowerCase())
            )
        }))
        .filter((dia) => dia.aulas.length > 0);

const horariosMiniKids = filtrarAulas((nome) =>
    nome.includes("mini kids")
);

const horariosKids1 = filtrarAulas((nome) =>
    nome.includes("kids 1")
);

const horariosKids2 = filtrarAulas((nome) =>
    nome.includes("kids 2")
);

const horariosKids3 = filtrarAulas((nome) =>
    nome.includes("kids 3")
);

const horariosTeens = filtrarAulas((nome) =>
    nome.includes("nova geração")
);

const horariosAdultos = horarios
    .map((dia) => ({
        ...dia,
        aulas: dia.aulas.filter((aula) => {
            const nome = aula.aula.toLowerCase();

            return (
                !nome.includes("mini kids") &&
                !nome.includes("kids 1") &&
                !nome.includes("kids 2") &&
                !nome.includes("kids 3") &&
                !nome.includes("nova geração")
            );
        })
    }))
    .filter((dia) => dia.aulas.length > 0);

const HorarioGrid = ({ data }: { data: Dia[] }) => (
    <div className="flex flex-wrap justify-center gap-6">
        {data.map((dia) => (
            <div
                key={dia.dia}
                className="w-full sm:w-[45%] lg:w-[30%] xl:w-[18%] bg-white rounded-2xl shadow-md overflow-hidden"
            >
                <div className="bg-black text-white py-3 px-4">
                    <h2 className="text-xl font-bold">{dia.dia}</h2>
                </div>

                <div className="divide-y divide-gray-200">
                    {dia.aulas.map((aula, index) => (
                        <div
                            key={index}
                            className="p-4 hover:bg-gray-50 transition-colors"
                        >
                            <div className="flex gap-4">
                                <span className="font-medium text-gray-900 w-14 shrink-0">
                                    {aula.horario}
                                </span>

                                <div>
                                    <p className="font-semibold text-gray-800">
                                        {aula.aula}
                                    </p>

                                    <p className="text-sm text-gray-500">
                                        {aula.professor}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ))}
    </div>
);

const Section = ({
    title,
    data
}: {
    title: string;
    data: Dia[];
}) => (
    <section className="space-y-6">
        <h2 className="text-3xl font-bold text-center">{title}</h2>

        <HorarioGrid data={data} />
    </section>
);

const HorariosTabs = () => {
    const [activeTab, setActiveTab] = useState<Tab>("Kids");

    return (
        <section className="max-w-7xl mx-auto px-6 py-12">
            <div className="flex flex-wrap justify-center gap-4 mb-12">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-6 py-3 rounded-2xl shadow-md font-semibold transition-all
                            ${
                                activeTab === tab
                                    ? "bg-black text-white border-black"
                                    : "bg-white text-black border-gray-300 hover:border-black"
                            }
                        `}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            <div className="space-y-16">
                <div className={activeTab === "Kids" ? "block" : "hidden"}>
                    <div className="bg-gray-100 border border-gray-200 rounded-2xl p-6 mb-12 shadow-sm max-w-auto md:max-w-4xl mx-auto text-center">
                        <h2 className="text-2xl font-bold mb-4">
                            Informações Importantes para Pais e Responsáveis
                        </h2>

                        <div className="space-y-4 text-gray-700 leading-relaxed">
                            <p>
                                As turmas infantis são organizadas por faixa etária e nível técnico.
                                Cada criança é matriculada em um horário específico, definido pela equipe
                                pedagógica da escola.
                            </p>

                            <p>
                                Isso permite um acompanhamento mais próximo do desenvolvimento técnico,
                                disciplina, adaptação e evolução individual de cada aluno.
                            </p>

                            <p>
                                O treino de sábado é um treino especial de integração, reunindo alunos
                                de diferentes turmas infantis em um ambiente mais dinâmico e interativo.
                            </p>

                            <p>
                                Em caso de dúvidas sobre qual turma é mais indicada para seu filho(a),
                                nossa equipe estará disponível para orientar.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-16">
                        <Section
                            title="Mini Kids (03 a 06 anos)"
                            data={horariosMiniKids}
                        />

                        <Section
                            title="Kids 1"
                            data={horariosKids1}
                        />

                        <Section
                            title="Kids 2"
                            data={horariosKids2}
                        />

                        <Section
                            title="Kids 3"
                            data={horariosKids3}
                        />

                        <Section
                            title="Treino de Integração Kids"
                            data={[{dia: "SAB", aulas: [{horario: "10h30", aula: "Jiu-Jitsu Kids", professor: "Prof. Johnny Lemes"}]}]}
                        />

                    </div>
                </div>

                <div className={activeTab === "Teens" ? "block" : "hidden"}>
                    <Section
                        title="Teens (12 a 16 anos)"
                        data={horariosTeens}
                    />
                </div>

                <div className={activeTab === "Adultos" ? "block" : "hidden"}>
                    <Section
                        title="Adultos"
                        data={horariosAdultos}
                    />
                </div>
            </div>
        </section>
    );
};

export default HorariosTabs;